import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../hooks/useAuth';
import { userService } from '../../../services/userService';
import { postService, Post, Comment } from '../../../services/postService';
import { courseService } from '../../../services/courseService';
import { taskService } from '../../../services/taskService';
import { useToast } from '../../../components/Toast';
import { Course, Level, UserLevel } from '../../../types';
import { useNavigate } from 'react-router-dom';

export const useProfile = () => {
    const { user, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const { showToast } = useToast();

    // Data States
    const [posts, setPosts] = useState<Post[]>([]);
    const [postComments, setPostComments] = useState<{ [postId: number]: Comment[] }>({});
    const [enrollments, setEnrollments] = useState<Course[]>([]);
    const [levels, setLevels] = useState<Level[]>([]);
    const [userLevel, setUserLevel] = useState<UserLevel | null>(null);

    // Loading States
    const [loading, setLoading] = useState(true);
    const [loadingPosts, setLoadingPosts] = useState(false);

    // fetch data
    const fetchData = useCallback(async () => {
        if (!user) return;

        try {
            setLoading(true);

            // 1. Fetch User Level Data
            try {
                const [myLevelData, levelsData] = await Promise.all([
                    taskService.getMyLevel(),
                    taskService.listLevels(),
                ]);
                setUserLevel(myLevelData);
                setLevels(levelsData.levels);
            } catch (err) {
                console.error('Error fetching level data:', err);
                // Fallback to user stats if available
                if (user.stats?.level) {
                    setUserLevel({
                        id: 0,
                        user_id: user.id,
                        level: user.stats.level,
                        total_points: user.stats.points || 0,
                        updated_at: new Date().toISOString(),
                        points_to_next: user.stats.points_to_next,
                    });
                }
            }

            // 2. Fetch User Profile & Stats (refresh)
            await userService.getUserProfile(user.id);

            // 3. Fetch Posts
            setLoadingPosts(true);
            const postsData = await postService.getUserPosts(user.id, { page: 1, per_page: 20 });
            setPosts(postsData.posts);

            // 4. Fetch Comments for Posts
            const commentsMap: { [postId: number]: Comment[] } = {};
            await Promise.all(postsData.posts.map(async (post: Post) => {
                try {
                    const comments = await postService.getComments(post.id);
                    commentsMap[post.id] = comments;
                } catch (error) {
                    commentsMap[post.id] = [];
                }
            }));
            setPostComments(commentsMap);

            // 5. Fetch Enrollments
            try {
                const allCourses = await courseService.getCourses(1, 100);
                const userEnrollments = allCourses.courses.filter((course: Course) =>
                    course.enrolled === true
                );
                setEnrollments(userEnrollments);
            } catch (error) {
                console.error('Error fetching enrollments:', error);
            }

        } catch (error) {
            console.error('Error fetching profile data:', error);
            showToast('common.errors.loadProfile', 'error');
        } finally {
            setLoading(false);
            setLoadingPosts(false);
        }
    }, [user, showToast]);

    useEffect(() => {
        if (!authLoading && !user) {
            navigate('/login');
            return;
        }
        if (user) {
            fetchData();
        }
    }, [user, authLoading, navigate, fetchData]);

    return {
        user,
        posts,
        setPosts,
        postComments,
        setPostComments,
        enrollments,
        levels,
        userLevel,
        loading,
        loadingPosts,
        refreshData: fetchData
    };
};
